import { useEffect, useContext, useCallback } from "react";
import { CartContext } from "./CartContext";

const useVariantManager = () => {
  const {
    variants,
    selectedVariant,
    setSelectedVariant,
    selectedAttributes,
    setSelectedAttributes,
  } = useContext(CartContext);

  // Select default attributes from first variant
  const setDefaultsFromVariants = useCallback(() => {
    if (Array.isArray(variants) && variants.length > 0) {
      const firstVariant = variants[0];
      // console.log("firstVariant", firstVariant);
      if (
        firstVariant &&
        Array.isArray(firstVariant.attr_val_data) &&
        firstVariant.attr_val_data.length > 0
      ) {
        const defaultAttributes = firstVariant.attr_val_data.reduce(
          (acc, { attribute_name, value_id }) => {
            if (attribute_name && value_id !== undefined) {
              acc[attribute_name] = value_id.toString();
            }
            return acc;
          },
          {}
        );
        setSelectedAttributes(defaultAttributes);
        setSelectedVariant(firstVariant);
      } else {
        setSelectedAttributes({});
        if (firstVariant) {
          // console.log("yes", firstVariant);
          setSelectedVariant(firstVariant);
        } else {
          // console.log("no");
          setSelectedVariant(null);
        }
      }
    } else {
      setSelectedAttributes({});
      setSelectedVariant(null);
    }
  }, [variants, setSelectedAttributes, setSelectedVariant]);

  // Update selected variant when selectedAttributes changes
  const updateSelectedVariant = useCallback(
    (updatedAttributes) => {
      if (!Array.isArray(variants)) return;
      const matchedVariant = variants.find((variant) =>
        variant.attr_val_data.every(
          ({ attribute_name, value_id }) =>
            updatedAttributes[attribute_name] === value_id.toString()
        )
      );
      setSelectedVariant(matchedVariant || null);
    },
    [variants, setSelectedVariant]
  );

  // Trigger default variant/attributes selection on variant change
  useEffect(() => {
    setDefaultsFromVariants();
  }, [variants, setDefaultsFromVariants]);

  // Trigger variant update on attribute change
  useEffect(() => {
    if (selectedAttributes && Object.keys(selectedAttributes).length > 0) {
      updateSelectedVariant(selectedAttributes);
    }
  }, [selectedAttributes, updateSelectedVariant, setSelectedVariant]);

  // Handle attribute change from UI
  const handleAttributeChange = (attributeName, valueId) => {
    setSelectedAttributes((prev) => ({
      ...prev,
      [attributeName]: valueId,
    }));
  };

  // Get available attribute options
  const getAvailableOptions = useCallback(() => {
    const attributeGroups = {};
    if (Array.isArray(variants)) {
      variants.forEach((variant) => {
        if (Array.isArray(variant.attr_val_data)) {
          variant.attr_val_data.forEach(
            ({ attribute_name, value_id, value_name }) => {
              if (attribute_name && value_id !== undefined) {
                if (!attributeGroups[attribute_name]) {
                  attributeGroups[attribute_name] = [];
                }
                if (
                  !attributeGroups[attribute_name].some(
                    (item) => item.value_id === value_id
                  )
                ) {
                  attributeGroups[attribute_name].push({
                    value_id,
                    value_name,
                  });
                }
              }
            }
          );
        }
      });
    }
    return attributeGroups;
  }, [variants]);

  return {
    selectedAttributes,
    handleAttributeChange,
    availableOptions: getAvailableOptions(),
    selectedVariant,
    setSelectedVariant,
  };
};

export default useVariantManager;
